import { supabase } from './supabase'
import CryptoJS from 'crypto-js'

class PasswordManagerService {
  constructor() {
    this.encryptionKey = null
  }

  // Generate salt for password hashing
  generateSalt() {
    return CryptoJS.lib.WordArray.random(256/8).toString()
  }

  // Hash master password with salt
  hashMasterPassword(password, salt) {
    return CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 10000
    }).toString()
  }

  // Generate encryption key from master password
  generateEncryptionKey(masterPassword, salt) {
    return CryptoJS.PBKDF2(masterPassword, salt, {
      keySize: 256/32,
      iterations: 10000
    }).toString()
  }

  // Encrypt data
  encrypt(data, key) {
    return CryptoJS.AES.encrypt(JSON.stringify(data), key).toString()
  }

  // Decrypt data
  decrypt(encryptedData, key) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, key)
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
    } catch (error) {
      throw new Error('Failed to decrypt data - invalid key')
    }
  }

  // Check if user has master password set
  async hasMasterPassword(userId) {
    try {
      const { data, error } = await supabase
        .from('password_manager_auth')
        .select('id')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error
      }

      return !!data
    } catch (error) {
      console.error('Error checking master password:', error)
      return false
    }
  }

  // Set up master password (first time)
  async setupMasterPassword(userId, masterPassword) {
    try {
      const salt = this.generateSalt()
      const hashedPassword = this.hashMasterPassword(masterPassword, salt)

      const { error } = await supabase
        .from('password_manager_auth')
        .insert({
          user_id: userId,
          password_hash: hashedPassword,
          salt: salt
        })

      if (error) throw error

      // Generate and store encryption key
      this.encryptionKey = this.generateEncryptionKey(masterPassword, salt)
      
      return true
    } catch (error) {
      console.error('Error setting up master password:', error)
      throw error
    }
  }

  // Verify master password
  async verifyMasterPassword(userId, masterPassword) {
    try {
      const { data, error } = await supabase
        .from('password_manager_auth')
        .select('password_hash, salt')
        .eq('user_id', userId)
        .single()

      if (error) throw error

      const hashedInput = this.hashMasterPassword(masterPassword, data.salt)
      
      if (hashedInput === data.password_hash) {
        // Generate encryption key for this session
        this.encryptionKey = this.generateEncryptionKey(masterPassword, data.salt)
        return true
      }
      
      return false
    } catch (error) {
      console.error('Error verifying master password:', error)
      return false
    }
  }

  // Save category
  async saveCategory(userId, category) {
    try {
      if (!this.encryptionKey) throw new Error('Not authenticated')

      const encryptedName = this.encrypt(category.name, this.encryptionKey)

      const { data, error } = await supabase
        .from('password_categories')
        .insert({
          user_id: userId,
          name: encryptedName
        })
        .select()
        .single()

      if (error) throw error

      return {
        id: data.id,
        name: category.name,
        createdAt: data.created_at
      }
    } catch (error) {
      console.error('Error saving category:', error)
      throw error
    }
  }

  // Load categories
  async loadCategories(userId) {
    try {
      if (!this.encryptionKey) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('password_categories')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

      if (error) throw error

      return data.map(category => ({
        id: category.id,
        name: this.decrypt(category.name, this.encryptionKey),
        createdAt: category.created_at
      }))
    } catch (error) {
      console.error('Error loading categories:', error)
      return []
    }
  }

  // Save password entry
  async savePassword(userId, password) {
    try {
      if (!this.encryptionKey) throw new Error('Not authenticated')

      const passwordData = {
        title: password.title,
        username: password.username,
        password: password.password,
        url: password.url,
        notes: password.notes
      }

      const encryptedData = this.encrypt(passwordData, this.encryptionKey)

      const { data, error } = await supabase
        .from('password_entries')
        .insert({
          user_id: userId,
          category_id: password.categoryId,
          encrypted_data: encryptedData
        })
        .select()
        .single()

      if (error) throw error

      return {
        id: data.id,
        ...passwordData,
        categoryId: password.categoryId,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      console.error('Error saving password:', error)
      throw error
    }
  }

  // Load password entries
  async loadPasswords(userId) {
    try {
      if (!this.encryptionKey) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('password_entries')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error

      return data.map(entry => {
        const decryptedData = this.decrypt(entry.encrypted_data, this.encryptionKey)
        return {
          id: entry.id,
          ...decryptedData,
          categoryId: entry.category_id,
          createdAt: entry.created_at,
          updatedAt: entry.updated_at
        }
      })
    } catch (error) {
      console.error('Error loading passwords:', error)
      return []
    }
  }

  // Delete category
  async deleteCategory(userId, categoryId) {
    try {
      const { error } = await supabase
        .from('password_categories')
        .delete()
        .eq('user_id', userId)
        .eq('id', categoryId)

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting category:', error)
      throw error
    }
  }

  // Delete password
  async deletePassword(userId, passwordId) {
    try {
      const { error } = await supabase
        .from('password_entries')
        .delete()
        .eq('user_id', userId)
        .eq('id', passwordId)

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting password:', error)
      throw error
    }
  }

  // Clear encryption key (logout)
  clearSession() {
    this.encryptionKey = null
  }
}

export const passwordManagerService = new PasswordManagerService()
